import React, { useEffect, useState } from 'react';
import Loader from '@lwt-helix/loader';
import { HelixIcon } from '@lwt-helix/helix-icon';
import { chevron_small_right } from '@lwt-helix/helix-icon/outlined';
import { useCommonStore,useSearchStore, useSavedAgentsStore } from '../../store/store';
import { getDashboardRecentActivity } from '../../service/service-gateway';
import { getActivityText } from '../../common/helpers/titles';
import { terms } from '../constants';
import { modules, moduleNames } from '../../constants';
import { useHistory } from 'react-router-dom';
import { Routes } from '../../common/routes/routes';
import { favoriteAgentTerms } from '../../agent-production/constants/agentProductionConstants';
import { useAuthStore } from '../../store/auth/store';

const RecruitingActivity = () => {
    const [activityData, setActivityData] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [error, setError] = useState(null);
    const{
        agentLists,
        timeIntervals,
        userProducts
    } = useCommonStore((state) =>({
        agentLists: state.agentLists,
        timeIntervals: state.timeIntervals,
        userProducts: state.privileges?.userProducts
    }));
    const history = useHistory();
    const isLegacyUser = useAuthStore((state) => state.isLegacyUser);

    const savedAgentsModule = modules.proficiencyMetrics.savedAgents;

    const { 
        selectedMls,
        setSelectedMls,
     } = useSearchStore((state) => ({
        selectedMls: state[state.selectedModule]?.selectedMls,
        setSelectedMls: state.setSelectedMls
    }));

    const {
        setSelectedListByModule,
    } = useSavedAgentsStore((state) => ({
        setSelectedListByModule: state.setSelectedListByModule,
    }));

    useEffect(() => {
        const fetchActivityData = async () => {
            const selectedMlsId = selectedMls?.mlsId.toString();
            if (!selectedMlsId) return;

            setIsLoadingData(true);
            setError(null);

            try {
                const response = await getDashboardRecentActivity(selectedMlsId);

                if (response?.error) {
                    throw new Error(terms.errorLoadingData);
                }

                setActivityData(response);
            } catch (err) {
                setError(terms.errorLoadingData);
                setActivityData(null);
            } finally {
                setIsLoadingData(false);
            }
        };

        fetchActivityData();
    }, [selectedMls?.mlsId]);

    const handleOnClick = async () => {
        const dashboardMlsId = selectedMls?.mlsId;
        const membership = useCommonStore.getState().membership;
        const selectedMlsObject = membership.find(
            (m) => m.mlsId === dashboardMlsId
        );
        const agentIdsForMls = agentLists
            ?.filter((list) => list.mlsId === dashboardMlsId)
            .map((list) => list.agentIds)
            .flat() || [];
        const uniqueAgentIds = [...new Set(agentIdsForMls)];
        const allListsForMls = {
            agentListId: `all-lists-${dashboardMlsId}`,
            name: favoriteAgentTerms.reservedListName,
            mlsId: dashboardMlsId,
            agentIds: uniqueAgentIds,
        };

        setSelectedMls(selectedMlsObject, savedAgentsModule, timeIntervals);
        setSelectedListByModule(savedAgentsModule, allListsForMls);
        history.push(Routes.PROF_METRICS.BASE + Routes.PROF_METRICS.SAVED_AGENTS + Routes.PROF_METRICS.ACTIVITY);
    }

    const renderEmptyState = () => {
        const hasNoSavedAgents = activityData?.savedAgentCount === 0;

        if (hasNoSavedAgents) {
            return (
                <div className="d-flex flex-column justify-content-center align-items-center text-center h-100 pb-5 px-3">
                    <h3 className="helix-body--strong mb-2">{terms.noAgentsTrackedYet}</h3>
                    <p className="helix-text-gray-500 mb-1">{terms.noAgentsOnYourRadarYet}</p>
                    <p className="helix-text-gray-500 mb-0">{terms.saveOneToFollowTheirActivity}</p>
                </div>
            );
        }

        return (
            <div className="d-flex flex-column justify-content-center align-items-center text-center h-100 pb-5 px-3">
                <h3 className="helix-body--strong mb-2">{terms.noNewActivity}</h3>
                <p className="helix-text-gray-500 mb-0">{terms.noNewActivityMessage}</p>
            </div>
        );
    };

    const renderActivities = () => {
        if (!activityData?.activities?.length) {
            return renderEmptyState();
        }

        const sortedActivities = [...activityData.activities]
            .sort((a, b) => {
                return new Date(b.date) - new Date(a.date);
            })
            .slice(0, 10);

        return (
            <>
                <div className="text-center py-3 mb-3">
                    <h2 className="mb-2">
                        {activityData?.summary?.activityCount || 0}
                    </h2>
                    <div className="helix-body--strong helix-text-gray-500">
                        {terms.savedAgentActivities}
                    </div>
                </div>
                <div className="d-flex flex-column">
                    {sortedActivities.map((activity, index) => (
                        <div
                            key={index}
                            className="pb-3 mb-3 recruiting-activity-item"
                        >
                            <div className="helix-body--strong mb-1">{`${activity?.agentFirstName} ${activity?.agentLastName}`}</div>
                            <div className="helix-text-gray-500 helix-body">
                                {getActivityText(activity)}
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };
    const hasProficiencyMetricsAccess = !!(userProducts?.includes(moduleNames.proficiencyMetrics) || isLegacyUser);
    const shouldShowSeeAllButton = !isLoadingData && !error && !!activityData?.activities?.length && hasProficiencyMetricsAccess;

    return (
        <div className="content d-flex flex-column h-100 p-4">
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h2 className="helix-display-x-small mb-1">{terms.recruitingActivity}</h2>
                    <p className="helix-small helix-text-gray-500 mb-0">{terms.last30Days}</p>
                </div>
                {shouldShowSeeAllButton && (
                    <button
                        className="helix-body--strong dashboard-nav-btn helix-brand-color"
                        onClick={handleOnClick}
                    >
                        {terms.seeAll}
                        <HelixIcon
                            icon={chevron_small_right}
                            className="ml-1"
                        />
                    </button>
                )}
            </div>

            <div className="d-flex flex-column flex-grow-1">
                {isLoadingData ? (
                    <div className="d-flex justify-content-center align-items-center pb-4">
                        <Loader />
                    </div>
                ) : error ? (
                    <div className="text-danger text-center p-3">{error}</div>
                ) : (
                    renderActivities()
                )}
            </div>
        </div>
    );
};

export default RecruitingActivity;
